<template>
    <card class="card-default">
        <card-header>Trending Over Time</card-header>
        <card-body class="pt-0" v-if="loading">
            <card-spinner size="large" style="height: 240px;"/>
        </card-body>
        <card-body class="p-3" v-if="!loading" style="max-height: 300px;">
            <bar-chart id="chartjs" :chart-data="chartData" :options="options" :width="210" :height="230"/>
        </card-body>
    </card>
</template>

<script>
    import {BarChart} from './../shared/charts';
    import {mapState} from 'vuex';

    export default {
        components: {BarChart},

        props: {
            type                    : {default: 'public'},
            maxOverall              : {default: 5},
            trendingOverTimeInterval: {type: Array, default: []},
            loading                 : {type: Boolean},
        },

        data: () => ({
            trendingOverTime: null
        }),


        computed: {
            ...mapState(['filter']),
            intervals() {
                return this.trendingOverTimeInterval.map(t => t.interval);
            },
            reviews() {
                return this.trendingOverTimeInterval.map(t => t.reviews);
            },
            averages() {
                return this.trendingOverTimeInterval.map(t => t.average);
            },
            promoters() {
                return this.trendingOverTimeInterval.map(t => t.promoter);
            },
            detractors() {
                return this.trendingOverTimeInterval.map(t => t.detractor);
            },
            neutrals() {
                return this.trendingOverTimeInterval.map(t => t.neutral);
            },
            weeks() {
                return this.trendingOverTimeInterval.map(t => t.week);
            },

            chartData() {
                return {
                    labels  : this.intervals,
                    datasets: [
                        {
                            type                     : 'line',
                            yAxisID                  : 'average',
                            afterLabel               : [{
                                promoter : {...this.promoters},
                                detractor: {...this.detractors},
                                neutral  : {...this.neutrals},
                                week     : {...this.weeks},
                                review   : {...this.reviews},
                            }],
                            label                    : ['Overall Score'],
                            data                     : this.averages,
                            backgroundColor          : 'rgba(2, 91, 175, .9)',
                            borderColor              : 'rgba(2, 91, 175, .9)',
                            fill                     : false,
                            lineTension              : 0,
                            pointBackgroundColor     : '#fff',
                            pointBorderColor         : 'rgba(2, 91, 175, .9)',
                            pointBorderWidth         : 2,
                            pointHoverBackgroundColor: '#fff',
                            pointRadius              : 5,
                            pointHoverRadius         : 6,
                        },
                        {
                            type           : 'bar',
                            yAxisID        : 'quantity',
                            label          : 'Reviews',
                            data           : this.reviews,
                            backgroundColor: 'rgba(230, 230, 230, .8)',
                        },
                    ],
                };
            },
            options() {
                return {
                    resposinve         : true,
                    maintainAspectRatio: false,
                    animation          : {
                        duration: 800,
                        easing  : 'easeInOutQuad',
                    },
                    scales             : {
                        yAxes: [
                            {
                                id        : 'quantity',
                                position  : 'left',
                                type      : 'linear',
                                ticks     : {
                                    beginAtZero        : true,
                                    responsive         : false,
                                    maintainAspectRatio: true,
                                },
                                scaleLabel: {
                                    display    : true,
                                    labelString: 'Reviews Quantity'
                                }
                            },
                            {
                                id        : 'average',
                                position  : 'right',
                                type      : 'linear',
                                ticks     : {
                                    beginAtZero        : true,
                                    max                : this.maxOverall,
                                    responsive         : false,
                                    maintainAspectRatio: true
                                },
                                scaleLabel: {
                                    display    : true,
                                    labelString: 'Overral Score'
                                }
                            },
                        ],
                        xAxes: [{
                            barPercentage: 0.5
                        }],
                    },
                    legend             : {
                        labels: {
                            fontColor: '#8c8c8c',
                            fontSize : 13,
                            boxWidth : 15,
                        },
                    },
                    tooltips           : {
                        mode   : 'index',
                        enabled: false,

                        custom(tooltipModel) {
                            let tooltipEl = document.getElementById('chartjs-tooltip');

                            if (!tooltipEl) {
                                tooltipEl                       = document.createElement('div');
                                tooltipEl.style.border          = '1px solid #bebeb8';
                                tooltipEl.style.zIndex          = '999';
                                tooltipEl.style.borderRadius    = '7px';
                                tooltipEl.style.backgroundColor = '#fff';
                                tooltipEl.style.boxShadow       = '0 5px 20px -8px rgba(0,0,0,0.3)';
                                tooltipEl.id                    = 'chartjs-tooltip';
                                tooltipEl.innerHTML             = `<table></table>`;
                                document.body.appendChild(tooltipEl);
                            }

                            if (tooltipModel.opacity === 0) {
                                tooltipEl.style.opacity    = 0;
                                tooltipEl.style.display    = 'none';
                                tooltipEl.style.transition = 'opacity 0.15s ease-in-out 0s, left 0s ease-in-out 0.15s, top 0s ease-in-out 0.15s';
                            } else {
                                tooltipEl.style.display = 'block';
                            }

                            const getBody = bodyItem => bodyItem.lines;

                            if (tooltipModel.body) {
                                let titleLines = tooltipModel.title || [];
                                let bodyLines  = tooltipModel.body.map(getBody)[0];
                                let innerHtml  = '<thead>';
                                const colors   = ['#025baf', '#c4c4c4', '#28a745', '#ffc107', '#dc3545'];
                                let weekTitle  = null;

                                if (bodyLines[0].match(/Week/gim)) {
                                    weekTitle = bodyLines[0].replace(/Week:\s+/gim, '');
                                    bodyLines.shift();
                                }

                                titleLines.forEach(
                                    title =>
                                        (innerHtml += `<tr><th style="font-size: 1.4em; text-align: center; padding: 0.5em 1em">${
                                            weekTitle === null ? title : weekTitle
                                            }</th></tr>`)
                                );

                                innerHtml += '</thead><tbody>';

                                bodyLines.forEach((body, i) => {
                                    let style = `
                                    width: 6px;
                                    height: 6px;
                                    background-color: ${colors[i]};
                                    display: inline-flex;
                                    align-items: center;
                                    margin: 5px;
                                    border-radius: 10px`;

                                    let roundedDiv = `<div style="${style}"></div>`;

                                    innerHtml += `<tr><td style="padding: 0.8em 1em; font-size: 1.1em">${roundedDiv}${body}</td></tr>`;
                                });

                                innerHtml += '</tbody>';

                                let tableRoot       = tooltipEl.querySelector('table');
                                tableRoot.innerHTML = innerHtml;
                            }

                            let position               = this._chart.canvas.getBoundingClientRect();
                            tooltipEl.style.opacity    = 1;
                            tooltipEl.style.position   = 'absolute';
                            tooltipEl.style.transform  = 'translateY(-50%)';
                            tooltipEl.style.left       = position.left + (tooltipModel.caretX + 20) + 'px';
                            tooltipEl.style.top        = position.top + tooltipModel.caretY + 'px';
                            tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                            tooltipEl.style.fontSize   = tooltipModel.bodyFontSize + 'px';
                            tooltipEl.style.fontStyle  = tooltipModel._bodyFontStyle;
                            tooltipEl.style.padding    = tooltipModel.yPadding + 'px' + tooltipModel.xPadding + 'px';
                        },

                        callbacks: {
                            label(tooltipItem, data) {
                                const dataset = data.datasets[tooltipItem.datasetIndex];
                                if (dataset.yAxisID === 'average') {
                                    const index        = tooltipItem.index;
                                    const datasetIndex = tooltipItem.datasetIndex;
                                    const afterLabel   = dataset.afterLabel[0];
                                    const hasWeek      = Object.values(afterLabel.week)
                                        .reduce((previous, current) => [].concat([], current));

                                    if (hasWeek[0] === undefined) {
                                        return [
                                            `${dataset.label[datasetIndex]}: ${dataset.data[index]}`,
                                            `Reviews: ${afterLabel.review[index]}`,
                                            `Promoter: ${afterLabel.promoter[index]}`,
                                            `Neutral: ${afterLabel.neutral[index]}`,
                                            `Detractor: ${afterLabel.detractor[index]}`,
                                        ];
                                    }

                                    return [
                                        `Week: ${afterLabel.week[index].startOfWeek} - ${afterLabel.week[index].endOfWeek}`,
                                        `${dataset.label[datasetIndex]}: ${dataset.data[index]}`,
                                        `Reviews: ${afterLabel.review[index]}`,
                                        `Promoter: ${afterLabel.promoter[index]}`,
                                        `Neutral: ${afterLabel.neutral[index]}`,
                                        `Detractor: ${afterLabel.detractor[index]}`,
                                    ];
                                }
                            },
                        },
                    },
                };
            },
        },
    };
</script>

<style lang="scss" scoped>
    .gray-btn {
        background-color: #ebebe9;
        color: #1a89e5;
        font-weight: bold;
        outline: none;
    }
</style>
